import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Button, Typography, Form } from 'antd'
import type { InputRef } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { isValid as isValidCpf } from '@fnando/cpf'
import type { AxiosError } from 'axios'
import { applyMask } from '@/lib/mask'
import { toast } from 'react-toastify'
import { useAuthStore } from '@/stores/authStore'
import { FormikInput } from '@/components/FormikInput'

const { Title } = Typography

const loginSchema = Yup.object({
  cpf: Yup.string()
    .required('CPF é obrigatório')
    .test('cpf-valid', 'CPF inválido', (value) => isValidCpf(value ?? '')),
  senha: Yup.string().required('Senha é obrigatória'),
})

// Espelha o ContractGuard: sem parceiro escolhido — administrador, ou usuário com mais de um
// vínculo — qualquer outro destino é uma tela de onde o guard expulsa de volta.
function destinoPosLogin(): string {
  const parceiro = useAuthStore.getState().parceiro
  if (!parceiro) return '/selecionar-parceiro'
  return parceiro.cpfAceiteContrato ? '/matricula' : '/contrato'
}

// O corpo de erro do Nest é um objeto (`{ statusCode, message, error }`), e o toast não renderiza
// objeto — passá-lo adiante engolia a mensagem em silêncio.
function mensagemDeErro(error: unknown): string {
  const data = (error as AxiosError).response?.data
  if (typeof data === 'string') return data

  const message = (data as { message?: string | string[] } | undefined)?.message
  if (Array.isArray(message)) return message.join(', ')
  return message ?? 'Erro ao fazer login'
}

// ─── Entry component ─────────────────────────────────────────────────────────

export function Login() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <LoginCard />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function LoginCard() {
  return (
    <Card className="w-full max-w-md shadow-md">
      <LoginHeader />
      <LoginForm />
    </Card>
  )
}

function LoginHeader() {
  return (
    <div className="text-center mb-6">
      <Title level={3} className="!mb-1 !text-[#1a3a5c]">
        New Driver Parceiros
      </Title>
      <p className="text-gray-500 text-sm">Acesse o painel de parceiros</p>
    </div>
  )
}

function LoginForm() {
  const login = useAuthStore((s) => s.login)
  const navigate = useNavigate()
  const cpfInputRef = useRef<InputRef>(null)

  useEffect(() => {
    if (cpfInputRef.current?.input) {
      applyMask(cpfInputRef.current.input, '999.999.999-99')
    }
  }, [])

  async function handleSubmit(values: { cpf: string; senha: string }) {
    try {
      await login(values.cpf, values.senha)
      navigate(destinoPosLogin(), { replace: true })
    } catch (error: unknown) {
      toast.error(mensagemDeErro(error))
    }
  }

  return (
    <Formik
      initialValues={{ cpf: '', senha: '' }}
      validationSchema={loginSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: submitForm, isSubmitting }) => (
        <Form layout="vertical" onFinish={submitForm}>
          <FormikInput
            ref={cpfInputRef}
            name="cpf"
            label="CPF"
            size="large"
            placeholder="000.000.000-00"
          />
          <FormikInput
            name="senha"
            label="Senha"
            type="password"
            size="large"
            placeholder="••••••••"
          />
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={isSubmitting}
            className="!bg-[#1a3a5c] hover:!bg-[#142d47]"
          >
            Entrar
          </Button>
        </Form>
      )}
    </Formik>
  )
}
